import React, { useEffect, useState } from "react";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Search, Trash2, Image as ImageIcon, UploadCloud, X } from "lucide-react";
import { PageHeader, Badge, EmptyState } from "@/components/Common";

const URGENCY = ["normal", "urgent", "critical"];

export default function StockRequests() {
  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [imagesPreview, setImagesPreview] = useState(null);
  const initial = {
    product_name: "", sku: "", quantity: 1, unit: "pcs", branch: "",
    urgency: "normal", reason: "", preferred_supplier_id: "",
  };
  const [form, setForm] = useState(initial);
  const [imageFiles, setImageFiles] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [{ data: r }, { data: b }, { data: s }] = await Promise.all([
        api.get("/stock-requests"), api.get("/branches"), api.get("/suppliers"),
      ]);
      if (!cancelled) { setItems(r); setBranches(b); setSuppliers(s); }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);
  const reload = () => setRefreshKey((k) => k + 1);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, quantity: parseInt(form.quantity, 10) || 1 };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      const { data: req } = await api.post("/stock-requests", payload);
      // Upload images and attach
      if (imageFiles.length > 0) {
        const fileIds = [];
        for (const f of imageFiles) {
          const fd = new FormData(); fd.append("file", f);
          const { data: up } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
          fileIds.push(up.id);
        }
        await api.post(`/stock-requests/${req.id}/images`, { file_ids: fileIds });
      }
      toast.success("Stock request raised");
      setOpen(false);
      setForm(initial);
      setImageFiles([]);
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const act = async (id, status) => {
    try {
      await api.post(`/stock-requests/${id}/action`, { status });
      toast.success(`Marked ${status}`);
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this request?")) return;
    await api.delete(`/stock-requests/${id}`); reload();
  };

  const filtered = items.filter((i) =>
    !q || `${i.product_name} ${i.sku} ${i.branch} ${i.reason}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader
        eyebrow="Procurement"
        title="Stock Requests"
        subtitle="Staff raise requests for missing stock — with photos of the empty shelf or product label. Procurement approves, orders, and fulfils."
        actions={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(initial); setImageFiles([]); } }}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-900 hover:bg-emerald-950 text-white" data-testid="stockreq-new-button">
                <Plus size={16} className="mr-1.5" /> Raise request
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display">Raise stock request</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3" data-testid="stockreq-form">
                <div>
                  <Label>Product name *</Label>
                  <Input required value={form.product_name}
                    onChange={(e) => setForm({ ...form, product_name: e.target.value })}
                    className="mt-1" data-testid="stockreq-product-input" placeholder="e.g. Brown Bread Loaf" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>SKU / Barcode (optional)</Label>
                    <Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="mt-1" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>Qty *</Label>
                      <Input type="number" required value={form.quantity}
                        onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <Label>Unit</Label>
                      <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="mt-1" />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Branch *</Label>
                    <Select value={form.branch} onValueChange={(v) => setForm({ ...form, branch: v })}>
                      <SelectTrigger className="mt-1" data-testid="stockreq-branch-select"><SelectValue placeholder="Choose" /></SelectTrigger>
                      <SelectContent>
                        {branches.map((b) => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Urgency</Label>
                    <Select value={form.urgency} onValueChange={(v) => setForm({ ...form, urgency: v })}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {URGENCY.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Preferred supplier (optional)</Label>
                  <Select value={form.preferred_supplier_id || ""} onValueChange={(v) => setForm({ ...form, preferred_supplier_id: v })}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Reason / context</Label>
                  <Textarea rows={2} value={form.reason}
                    onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1"
                    placeholder="e.g. Shelf empty since Monday — high customer demand" />
                </div>
                <div className="bg-amber-50/40 border border-dashed border-amber-300 rounded-md p-3">
                  <Label className="flex items-center gap-1.5 text-sm">
                    <ImageIcon size={14} className="text-amber-700" /> Photos of missing product / empty shelf
                  </Label>
                  <Input type="file" multiple accept="image/*" capture="environment"
                    onChange={(e) => setImageFiles(Array.from(e.target.files || []))}
                    className="mt-2" data-testid="stockreq-image-input" />
                  {imageFiles.length > 0 && (
                    <div className="text-xs text-gray-600 mt-2 flex flex-wrap gap-1.5">
                      {imageFiles.map((f, i) => (
                        <span key={i} className="bg-white border border-amber-200 rounded px-2 py-0.5">{f.name}</span>
                      ))}
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={busy}
                    className="bg-emerald-900 hover:bg-emerald-950 text-white"
                    data-testid="stockreq-submit-button">
                    {busy ? "Submitting…" : "Submit request"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="bg-white border border-gray-200 rounded-md p-3 mb-4 flex gap-3 items-center">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 h-9" />
        </div>
        <span className="text-xs text-gray-500">{filtered.length} request(s)</span>
      </div>

      {filtered.length === 0 ? (
        <EmptyState title="No stock requests yet" hint="Click “Raise request” when shelves are empty — attach a photo for context." />
      ) : (
        <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
          <table className="min-w-full text-sm" data-testid="stockreq-table">
            <thead className="bg-gray-50">
              <tr>{["Product", "Qty", "Branch", "Urgency", "Photos", "Status", "Actions", ""].map((h) => (
                <th key={h} className="text-left text-xs font-bold text-gray-600 uppercase tracking-wider px-4 py-3">{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{r.product_name}</div>
                    <div className="text-xs text-gray-500">{r.reason || "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{r.quantity} {r.unit || ""}</td>
                  <td className="px-4 py-3 text-gray-700">{r.branch}</td>
                  <td className="px-4 py-3">
                    <Badge tone={r.urgency === "critical" ? "red" : r.urgency === "urgent" ? "amber" : "slate"}>{r.urgency}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    {(r.image_file_ids || []).length > 0 ? (
                      <button onClick={() => setImagesPreview(r)}
                        className="inline-flex items-center gap-1 text-xs text-emerald-900 hover:underline"
                        data-testid={`stockreq-images-${r.id}`}>
                        <ImageIcon size={12} /> {r.image_file_ids.length}
                      </button>
                    ) : <span className="text-xs text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={r.status === "fulfilled" ? "emerald" : r.status === "rejected" ? "red" : r.status === "ordered" ? "blue" : r.status === "approved" ? "blue" : "amber"}>
                      {r.status || "pending"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {[["approve", "approved"], ["order", "ordered"], ["fulfill", "fulfilled"], ["reject", "rejected"]].map(([label, target]) => (
                        <Button key={label} size="sm" variant="outline" onClick={() => act(r.id, target)}
                          className="h-7 px-2 text-xs"
                          data-testid={`stockreq-${label}-${r.id}`}>{label}</Button>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => remove(r.id)}
                      className="text-red-700 hover:bg-red-50">
                      <Trash2 size={13} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {imagesPreview && (
        <Dialog open onOpenChange={(v) => !v && setImagesPreview(null)}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle className="font-display flex items-center justify-between">
                <span>{imagesPreview.product_name} — photos</span>
                <Button variant="ghost" size="sm" onClick={() => setImagesPreview(null)}>
                  <X size={14} />
                </Button>
              </DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {(imagesPreview.image_file_ids || []).map((fid) => (
                <a key={fid} href={fileUrl(fid)} target="_blank" rel="noreferrer"
                  className="block aspect-square bg-gray-100 rounded-md overflow-hidden border border-gray-200">
                  <img src={fileUrl(fid)} alt="" className="w-full h-full object-cover" />
                </a>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
